import { ColDef, PostSortRowsParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import {
	CreateCellClickHandler, createCellCssClassRules, CustomCellClickAction,
	GridCellConfig, getSwitchButtonColumnOptions, postSortHandler,
} from 'src/utils/grid/GridHandlerContent';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { formatDate } from 'src/utils/string/stringUtil';
import { EQM3010_R01Dto, defaultEQM3010_R01Dto } from './Dto_EQM3010_R01';
import { useEQM3010_R01Store } from './Store_EQM3010_R01';
import { useEQM3010_R01CellChange } from './hooks/EQM3010_R01CellChange';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';

/** 로컬스토리지 컬럼 너비·순서 저장 키 */
const COLUMN_STATE_KEY = 'EQM3010_R01-grid-column-state';

/** 월별 검교정 계획 필드명 (1월~12월) */
const MONTH_FIELDS = [
	'month01', 'month02', 'month03', 'month04', 'month05', 'month06',
	'month07', 'month08', 'month09', 'month10', 'month11', 'month12',
] as const;

/** MONTH_FIELDS 요소 타입 (switchMonthCol field 파라미터용) */
type MonthField = typeof MONTH_FIELDS[number];

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<EQM3010_R01Dto> | null>;
}

/**
 * EQM3010 검교정 계획 등록 그리드
 * - 조회된 계측기별 행에 월별 계획(Y/N 스위치)·비고 입력
 * - 계측기·교정일 정보는 읽기 전용
 */
export const Table_EQM3010_R01 = ({ clickable, setClickable, gridRef }: Props) => {
	const {
		EQM3010_R01List, setEQM3010_R01List,
		updateEQM3010_R01List,
		deletedEQM3010_R01List,
		selectedEQM3010_R01List, setSelectedEQM3010_R01List,
		initSelectedEQM3010_R01List, allSelectedEQM3010_R01List,
		originalEQM3010_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useEQM3010_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	/** 행 ID·패딩·순번 렌더·컬럼 병합·월 스위치 토글 등 그리드 공통 유틸 */
	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: EQM3010_R01List,
		defaultGridData: defaultEQM3010_R01Dto,
		selectedRow: selectedEQM3010_R01List,
		originalGridData: originalEQM3010_R01List,
		updateRow: (index, data) => updateEQM3010_R01List(index, data),
	});

	/** 컬럼 상태 복원 시 사용할 헤더명 매핑 */
	const headerNames: Record<string, string> = {
		rowIndex: '', compNm: '사업장', equipmentCd: '코드', equipmentNm: '용도',
		gaugeCalibDt: '최종교정일자', gaugeCalibPlanDt: '차기교정일',
		month01: '1월', month02: '2월', month03: '3월', month04: '4월', month05: '5월', month06: '6월',
		month07: '7월', month08: '8월', month09: '9월', month10: '10월', month11: '11월', month12: '12월',
		remark: '비고',
	};

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['compNm', 'equipmentCd', 'equipmentNm', 'gaugeCalibDt', 'gaugeCalibPlanDt'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['compNm', 'equipmentCd', 'equipmentNm', 'gaugeCalibDt', 'gaugeCalibPlanDt'],
		pointerCells: [...MONTH_FIELDS, 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['equipmentNm', 'remark'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	/** 전 컬럼 공통: 선택·삭제·수정·비활성·포인터 커서 스타일 */
	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM3010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedEQM3010_R01List,
			deletedList: deletedEQM3010_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns, alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedEQM3010_R01List, deletedEQM3010_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns, alignLeftColumns,
		createCellClassRules,
	]);

	/** 비고 셀 값 변경 시 isModified 반영 */
	const { handleCellChange } = useEQM3010_R01CellChange({
		EQM3010_R01List, updateEQM3010_R01List,
	});

	/** 월별 Y/N 스위치 컬럼 정의 생성 (SYS2010 getSwitchButtonColumnOptions 패턴) */
	const switchMonthCol = useCallback(
		(field: MonthField, header: string): ColDef<EQM3010_R01Dto> => ({
			headerName: header,
			colId: field,
			field,
			width: 80,
			...getSwitchButtonColumnOptions(
				field, handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true,
			),
		}), [handleSwitchButtonHandler, headerNames]);

	/** 그리드 기본 컬럼 정의 (순번·계측기 정보·월 스위치·비고) */
	const baseColumnDefs: ColDef<EQM3010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			/** 순번 컬럼: 선택·삭제·수정 상태 표시 (행 추가/삭제 버튼 없음) */
			(params) => renderIndexCell(
				params,
				(inner) => getRowStyleFlags(inner, {
					deletedList: deletedEQM3010_R01List,
					selectedList: selectedEQM3010_R01List,
					dataList: EQM3010_R01List,
				}),
				() => {},
				() => {},
			),
		),
		{ headerName: '사업장', colId: 'compNm', field: 'compNm', width: 80, editable: false },
		{ headerName: '코드', colId: 'equipmentCd', field: 'equipmentCd', width: 100, editable: false },
		{ headerName: '용도', colId: 'equipmentNm', field: 'equipmentNm', width: 150, editable: false },
		{
			headerName: '최종교정일자', colId: 'gaugeCalibDt', field: 'gaugeCalibDt', width: 110, editable: false,
			valueFormatter: (p) => (p.data?.gaugeCalibDt ? formatDate(new Date(p.data.gaugeCalibDt)) : ''),
		},
		{
			headerName: '차기교정일', colId: 'gaugeCalibPlanDt', field: 'gaugeCalibPlanDt', width: 110, editable: false,
			valueFormatter: (p) => (p.data?.gaugeCalibPlanDt ? formatDate(new Date(p.data.gaugeCalibPlanDt)) : ''),
		},
		switchMonthCol('month01', '1월'), switchMonthCol('month02', '2월'), switchMonthCol('month03', '3월'),
		switchMonthCol('month04', '4월'), switchMonthCol('month05', '5월'), switchMonthCol('month06', '6월'),
		switchMonthCol('month07', '7월'), switchMonthCol('month08', '8월'), switchMonthCol('month09', '9월'),
		switchMonthCol('month10', '10월'), switchMonthCol('month11', '11월'), switchMonthCol('month12', '12월'),
		{ headerName: '비고', colId: 'remark', field: 'remark', width: 150, editable: true, pasteable: true },
	], [deletedEQM3010_R01List, selectedEQM3010_R01List, EQM3010_R01List, renderIndexCell, switchMonthCol]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	/** baseColumnDefs + 로컬스토리지 columnState 병합 */
	const columnDefs = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<EQM3010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames]);

	/**
	 * 셀 클릭 시 컬럼별 분기
	 * - 비고: 인라인 에디터 시작
	 * - 월 스위치: getSwitchButtonColumnOptions 내부 onCellClicked 처리
	 */
	const cellClickActions: CustomCellClickAction<EQM3010_R01Dto>[] = useMemo(() => [
		{
			validate: (field) => field === 'remark',
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: 'remark' }); },
		},
	], []);

	/** cellClickActions를 묶은 AG Grid onCellClicked 핸들러 */
	const handleCellClick = CreateCellClickHandler(EQM3010_R01List, 'valueIndex', cellClickActions);

	/** 정렬 후 orderIndex를 원본 순서 기준으로 복원 */
	const handlePostSort = useCallback((params: PostSortRowsParams<EQM3010_R01Dto>) => {
		postSortHandler(params, EQM3010_R01List, originalEQM3010_R01List, setEQM3010_R01List);
	}, [EQM3010_R01List, originalEQM3010_R01List, setEQM3010_R01List]);

	/** Shift+클릭 범위 행 선택 설정 */
	const shiftRangeSelectionConfig = useMemo(() => ({
		enableShiftRangeSelect: true,
		idKey: 'valueIndex' as const,
		selectedIds: selectedEQM3010_R01List as Array<string | number>,
		setSelectedIds: (ids: Array<string | number>) => setSelectedEQM3010_R01List(ids as string[]),
		/** 행 클릭 선택 가능 여부 (순번 버튼 클릭 직후 1회 차단) */
		isRowSelectable: (event: RowClickedEvent<EQM3010_R01Dto>) => {
			if (!clickable) {
				// 순번 영역 클릭 직후 발생하는 행 선택 이벤트 1회 무시
				setClickable(true);
				return false;
			}
			return !!event.data;
		},
	}), [selectedEQM3010_R01List, setSelectedEQM3010_R01List, clickable, setClickable]);

	/** 하단 고정 합계 행 (조회 건수 표시) */
	const pinnedBottomRow = useMemo(() => ([{
		equipmentNm: `${EQM3010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [EQM3010_R01List]);

	return (
		<GridContextWrapper<EQM3010_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setEQM3010_R01List}
			initSelected={initSelectedEQM3010_R01List}
			defaultRowData={defaultEQM3010_R01Dto}
			allSelected={allSelectedEQM3010_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<EQM3010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				getRowId={getRowId}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				rowClassRules={createRowClassRules<EQM3010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedEQM3010_R01List,
					deletedList: deletedEQM3010_R01List,
				})}
				shiftRangeSelection={shiftRangeSelectionConfig}
				onCellClicked={handleCellClick}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				onPostSortRows={handlePostSort}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
